export const FARM_ASSET_CONTRACT_ID = "NT-I0-ASSET-INGRESS-V1" as const;
export const FARM_ASSET_CONTRACT_SHA256 = "5B22AA8507B8B71B9083FC16B164A4932344563484241D6E55CC17D70D9FE23A" as const;
export const FARM_ASSET_THREE_REVISION = "185" as const;
export const NAVIGATION_PADDING_MICROWU = 300_000 as const;

export type FarmAssetFamily =
  | "player" | "generalist" | "chicken" | "corn-stalk-cluster" | "carry-unit"
  | "major-node" | "fence-gate-sign-module" | "decorative-prop-cluster" | "vfx-burst";
export type Vec3I = { x: number; y: number; z: number };
export type AabbI = { max: Vec3I; min: Vec3I };
export type TextureDescriptor = {
  atlasId: string | null;
  bytes: number;
  faces: number;
  format: "r8-unorm" | "rg8-unorm" | "rgba8-unorm" | "srgb8-alpha8";
  height: number;
  layers: number;
  mipmapped: boolean;
  sourceSha256: string;
  textureId: string;
  width: number;
};
export type MaterialDescriptor = { materialId: string; textureIds: string[]; type: string };
export type GeometryDescriptor = {
  assetNodeId: string;
  drawSubmissions: number;
  instanceCount: number;
  materialIds: string[];
  primitiveElementCount: number;
  triangles: number;
  type: "Mesh" | "SkinnedMesh" | "InstancedMesh";
};
export type InteractionDescriptor = {
  assetNodeId: string;
  dimensionsMicroWU: { depth: number; height: number; width: number } | { radius: number } | { cylinderHeight: number; radius: number };
  isTrigger: true;
  role: "interaction" | "work-sensor";
  shape: "box" | "sphere" | "capsule";
};
export type FarmAssetManifest = {
  bottomPivotMicroWU: Vec3I;
  boundsMicroWU: AabbI;
  budget: {
    atlasId: string | null;
    drawSubmissions: number;
    materials: number;
    profile: FarmAssetFamily;
    textureBytes: number;
    triangles: number;
  };
  contractId: typeof FARM_ASSET_CONTRACT_ID;
  contractSha256: typeof FARM_ASSET_CONTRACT_SHA256;
  family: FarmAssetFamily;
  geometry: GeometryDescriptor[];
  id: string;
  interaction: InteractionDescriptor[];
  lod0RootAssetNodeId: string;
  manifestSha256: string;
  materials: MaterialDescriptor[];
  navigation: null | {
    paddedObstacleFootprintMicroWU: { maxX: number; maxZ: number; minX: number; minZ: number };
    paddingAppliedMicroWU: typeof NAVIGATION_PADDING_MICROWU;
    rawObstacleAabbMicroWU: AabbI;
  };
  provenance: {
    authorshipAttestation: string;
    generatedAt: string;
    generationRoute: string;
    generatorCommit: string;
    promptOrBriefPath: string;
    promptOrBriefSha256: string;
    referenceInputs: string[];
    rightsBasis: string;
    sourcePath: string;
    sourceSha256: string;
  };
  review: { canonicalGeometrySha256: string; fixtureSha256: string; socketChainSha256: string };
  revision: number;
  runtime: { factoryId: string | null; runtimeUrl: string | null };
  stack: {
    centerlineToleranceMicroWU: 20_000;
    maxCount: number;
    stackClearanceMicroWU: number;
  };
  status: "accepted";
  textures: TextureDescriptor[];
  threeRevision: typeof FARM_ASSET_THREE_REVISION;
  topSocketMicroWU: Vec3I;
};

const PROFILE_LIMITS: Record<FarmAssetFamily, { draws: number; materials: number; textureBytes: number | null; triangles: number }> = {
  player: { triangles: 7500, materials: 3, draws: 3, textureBytes: 1_048_576 },
  generalist: { triangles: 6500, materials: 3, draws: 3, textureBytes: 1_048_576 },
  chicken: { triangles: 2400, materials: 2, draws: 2, textureBytes: 524_288 },
  "corn-stalk-cluster": { triangles: 900, materials: 1, draws: 1, textureBytes: 524_288 },
  "carry-unit": { triangles: 320, materials: 1, draws: 1, textureBytes: null },
  "major-node": { triangles: 12000, materials: 4, draws: 4, textureBytes: 2_097_152 },
  "fence-gate-sign-module": { triangles: 2000, materials: 2, draws: 2, textureBytes: 1_048_576 },
  "decorative-prop-cluster": { triangles: 1500, materials: 2, draws: 2, textureBytes: 1_048_576 },
  "vfx-burst": { triangles: 1200, materials: 1, draws: 1, textureBytes: 524_288 },
};
const REQUIRED_ATLAS: Partial<Record<FarmAssetFamily, string>> = {
  generalist: "generalist-shared-atlas-v1", chicken: "chicken-shared-atlas-v1", "corn-stalk-cluster": "corn-shared-atlas-v1",
  "carry-unit": "sunpatch-shared-palette-v1", "fence-gate-sign-module": "fence-gate-sign-shared-atlas-v1",
  "decorative-prop-cluster": "decorative-shared-atlas-v1", "vfx-burst": "vfx-shared-atlas-v1",
};
export const FARM_ASSET_PROFILE_LIMITS = PROFILE_LIMITS;

function assertRecord(value: unknown, keys: readonly string[], label: string): asserts value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    throw new Error(`${label}: malformed-schema`);
  }
  const allowed = new Set(keys);
  for (const key of Object.keys(value)) if (!allowed.has(key)) throw new Error(`${label}: unknown-property:${key}`);
}
function integer(value: unknown, label: string, min = Number.MIN_SAFE_INTEGER): asserts value is number {
  if (!Number.isSafeInteger(value) || Object.is(value, -0) || (value as number) < min) throw new Error(`${label}: malformed-schema`);
}
function text(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || hasLoneSurrogate(value)) throw new Error(`${label}: malformed-schema`);
}
function hasLoneSurrogate(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) return true;
  }
  return false;
}
function sha(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || !/^[0-9a-f]{64}$/.test(value)) throw new Error(`${label}: malformed-schema`);
}
function vec3(value: unknown, label: string): asserts value is Vec3I {
  assertRecord(value, ["x", "y", "z"], label);
  integer(value.x, `${label}.x`); integer(value.y, `${label}.y`); integer(value.z, `${label}.z`);
}
function aabb(value: unknown, label: string): asserts value is AabbI {
  assertRecord(value, ["max", "min"], label); vec3(value.min, `${label}.min`); vec3(value.max, `${label}.max`);
  if (value.min.x >= value.max.x || value.min.y >= value.max.y || value.min.z >= value.max.z) throw new Error(`${label}: degenerate`);
}
function stringArray(value: unknown, label: string): asserts value is string[] {
  if (!Array.isArray(value)) throw new Error(`${label}: malformed-schema`);
  for (const item of value) text(item, label);
}

export function validateFarmAssetManifest(value: unknown): FarmAssetManifest {
  assertRecord(value, [
    "bottomPivotMicroWU", "boundsMicroWU", "budget", "contractId", "contractSha256", "family", "geometry", "id",
    "interaction", "lod0RootAssetNodeId", "manifestSha256", "materials", "navigation", "provenance", "review", "revision", "runtime", "stack",
    "status", "textures", "threeRevision", "topSocketMicroWU",
  ], "manifest");
  if (value.contractId !== FARM_ASSET_CONTRACT_ID || value.contractSha256 !== FARM_ASSET_CONTRACT_SHA256 ||
      value.threeRevision !== FARM_ASSET_THREE_REVISION || value.status !== "accepted") throw new Error("manifest: contract-mismatch");
  text(value.id, "manifest.id");
  text(value.lod0RootAssetNodeId, "manifest.lod0RootAssetNodeId");
  if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(value.id)) throw new Error("manifest.id: malformed-schema");
  if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(value.lod0RootAssetNodeId)) throw new Error("manifest.lod0RootAssetNodeId: malformed-schema");
  integer(value.revision, "manifest.revision", 1);
  if (typeof value.family !== "string" || !(value.family in PROFILE_LIMITS)) throw new Error("manifest.family: malformed-schema");
  const family = value.family as FarmAssetFamily;
  sha(value.manifestSha256, "manifest.manifestSha256");
  vec3(value.bottomPivotMicroWU, "manifest.bottomPivotMicroWU"); vec3(value.topSocketMicroWU, "manifest.topSocketMicroWU");
  aabb(value.boundsMicroWU, "manifest.boundsMicroWU");
  if (value.bottomPivotMicroWU.x !== 0 || value.bottomPivotMicroWU.y !== 0 || value.bottomPivotMicroWU.z !== 0) throw new Error("manifest: tampered-pivot");
  const height = value.boundsMicroWU.max.y - value.boundsMicroWU.min.y;
  if (value.topSocketMicroWU.x !== 0 || value.topSocketMicroWU.y !== height || value.topSocketMicroWU.z !== 0 ||
      value.boundsMicroWU.min.y !== 0 || value.boundsMicroWU.min.x + value.boundsMicroWU.max.x !== 0 ||
      value.boundsMicroWU.min.z + value.boundsMicroWU.max.z !== 0) throw new Error("manifest: tampered-socket");

  assertRecord(value.stack, ["centerlineToleranceMicroWU", "maxCount", "stackClearanceMicroWU"], "manifest.stack");
  if (value.stack.centerlineToleranceMicroWU !== 20_000) throw new Error("manifest.stack: malformed-schema");
  integer(value.stack.maxCount, "manifest.stack.maxCount", 1); integer(value.stack.stackClearanceMicroWU, "manifest.stack.stackClearanceMicroWU", 0);
  if (value.stack.maxCount > 8) throw new Error("manifest.stack: malformed-schema");
  assertRecord(value.runtime, ["factoryId", "runtimeUrl"], "manifest.runtime");
  if (!((typeof value.runtime.factoryId === "string" && value.runtime.runtimeUrl === null) ||
        (value.runtime.factoryId === null && typeof value.runtime.runtimeUrl === "string"))) throw new Error("manifest.runtime: malformed-schema");

  if (!Array.isArray(value.geometry) || value.geometry.length === 0) throw new Error("manifest.geometry: malformed-schema");
  const nodeIds = new Set<string>([value.lod0RootAssetNodeId]);
  for (const [index, item] of value.geometry.entries()) {
    assertRecord(item, ["assetNodeId", "drawSubmissions", "instanceCount", "materialIds", "primitiveElementCount", "triangles", "type"], `geometry[${index}]`);
    text(item.assetNodeId, "geometry.assetNodeId"); stringArray(item.materialIds, "geometry.materialIds");
    integer(item.drawSubmissions, "geometry.drawSubmissions", 1); integer(item.instanceCount, "geometry.instanceCount", 1);
    integer(item.primitiveElementCount, "geometry.primitiveElementCount", 3); integer(item.triangles, "geometry.triangles", 1);
    if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(item.assetNodeId) || nodeIds.has(item.assetNodeId) || item.materialIds.length === 0 || new Set(item.materialIds).size !== item.materialIds.length || item.materialIds.some((id) => !/^[a-z0-9][a-z0-9-]{1,63}$/.test(id))) throw new Error("geometry: malformed-schema");
    nodeIds.add(item.assetNodeId);
    if (item.primitiveElementCount % 3 !== 0 || item.triangles !== item.primitiveElementCount / 3 * item.instanceCount) throw new Error("geometry: invalid-budget");
    if (item.type !== "Mesh" && item.type !== "SkinnedMesh" && item.type !== "InstancedMesh") throw new Error("geometry.type: malformed-schema");
  }
  if (!Array.isArray(value.materials) || !Array.isArray(value.textures)) throw new Error("manifest descriptors: malformed-schema");
  const materialIds = new Set<string>(); const textureIds = new Set<string>();
  for (const item of value.materials) {
    assertRecord(item, ["materialId", "textureIds", "type"], "material"); text(item.materialId, "materialId"); text(item.type, "material.type"); stringArray(item.textureIds, "material.textureIds");
    if (item.type !== "MeshStandardMaterial" && item.type !== "MeshLambertMaterial" && item.type !== "MeshBasicMaterial") throw new Error("material.type: malformed-schema");
    if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(item.materialId) || materialIds.has(item.materialId) || new Set(item.textureIds).size !== item.textureIds.length || item.textureIds.some((id) => !/^[a-z0-9][a-z0-9-]{1,63}$/.test(id))) throw new Error("material: malformed-schema");
    materialIds.add(item.materialId);
  }
  for (const item of value.textures) {
    assertRecord(item, ["atlasId", "bytes", "faces", "format", "height", "layers", "mipmapped", "sourceSha256", "textureId", "width"], "texture");
    if (item.atlasId !== null) text(item.atlasId, "texture.atlasId");
    integer(item.bytes, "texture.bytes", 1); integer(item.faces, "texture.faces", 1); integer(item.height, "texture.height", 1);
    integer(item.layers, "texture.layers", 1); integer(item.width, "texture.width", 1); text(item.format, "texture.format");
    if (item.faces !== 1 && item.faces !== 6) throw new Error("texture.faces: malformed-schema");
    if (item.format !== "r8-unorm" && item.format !== "rg8-unorm" && item.format !== "rgba8-unorm" && item.format !== "srgb8-alpha8") throw new Error("texture.format: malformed-schema");
    if (typeof item.mipmapped !== "boolean") throw new Error("texture.mipmapped: malformed-schema");
    sha(item.sourceSha256, "texture.sourceSha256"); text(item.textureId, "texture.textureId");
    const bytesPerTexel = item.format === "r8-unorm" ? 1 : item.format === "rg8-unorm" ? 2 : 4;
    let expectedBytes = 0; let mipWidth = item.width; let mipHeight = item.height;
    for (;;) { expectedBytes += mipWidth * mipHeight * item.layers * item.faces * bytesPerTexel; if (!item.mipmapped || (mipWidth === 1 && mipHeight === 1)) break; mipWidth = Math.max(1, Math.floor(mipWidth / 2)); mipHeight = Math.max(1, Math.floor(mipHeight / 2)); }
    if (!Number.isSafeInteger(expectedBytes) || item.bytes !== expectedBytes) throw new Error("texture: invalid-budget");
    if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(item.textureId) || textureIds.has(item.textureId)) throw new Error("texture: malformed-schema");
    textureIds.add(item.textureId);
  }
  if (value.geometry.some((item) => (item as GeometryDescriptor).materialIds.some((id) => !materialIds.has(id))) || value.materials.some((item) => (item as MaterialDescriptor).textureIds.some((id) => !textureIds.has(id)))) throw new Error("manifest descriptors: dangling-reference");

  if (!Array.isArray(value.interaction)) throw new Error("manifest.interaction: malformed-schema");
  const width = value.boundsMicroWU.max.x - value.boundsMicroWU.min.x;
  const depth = value.boundsMicroWU.max.z - value.boundsMicroWU.min.z;
  for (const item of value.interaction) {
    assertRecord(item, ["assetNodeId", "dimensionsMicroWU", "isTrigger", "role", "shape"], "interaction");
    text(item.assetNodeId, "interaction.assetNodeId");
    if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(item.assetNodeId) || !nodeIds.has(item.assetNodeId)) throw new Error("interaction: invalid-trigger");
    if (item.isTrigger !== true || (item.role !== "interaction" && item.role !== "work-sensor")) throw new Error("interaction: invalid-trigger");
    if (item.shape === "box") {
      assertRecord(item.dimensionsMicroWU, ["depth", "height", "width"], "interaction.dimensions");
      if (item.dimensionsMicroWU.width !== width || item.dimensionsMicroWU.height !== height || item.dimensionsMicroWU.depth !== depth) throw new Error("interaction: invalid-trigger");
    } else if (item.shape === "sphere") {
      assertRecord(item.dimensionsMicroWU, ["radius"], "interaction.dimensions");
      if (item.dimensionsMicroWU.radius !== Math.floor((Math.max(width, height, depth) + 1) / 2)) throw new Error("interaction: invalid-trigger");
    } else if (item.shape === "capsule") {
      assertRecord(item.dimensionsMicroWU, ["cylinderHeight", "radius"], "interaction.dimensions");
      const radius = Math.floor((Math.min(width, depth) + 1) / 2);
      if (item.dimensionsMicroWU.radius !== radius || item.dimensionsMicroWU.cylinderHeight !== Math.max(0, height - 2 * radius)) throw new Error("interaction: invalid-trigger");
    } else throw new Error("interaction: invalid-trigger");
  }

  if (value.navigation !== null) {
    assertRecord(value.navigation, ["paddedObstacleFootprintMicroWU", "paddingAppliedMicroWU", "rawObstacleAabbMicroWU"], "navigation");
    if (value.navigation.paddingAppliedMicroWU !== NAVIGATION_PADDING_MICROWU) throw new Error("navigation: invalid-navigation");
    aabb(value.navigation.rawObstacleAabbMicroWU, "navigation.raw");
    if (canonicalManifestJson(value.navigation.rawObstacleAabbMicroWU) !== canonicalManifestJson(value.boundsMicroWU)) throw new Error("navigation: invalid-navigation");
    assertRecord(value.navigation.paddedObstacleFootprintMicroWU, ["maxX", "maxZ", "minX", "minZ"], "navigation.padded");
    const padded = value.navigation.paddedObstacleFootprintMicroWU;
    for (const key of ["maxX", "maxZ", "minX", "minZ"] as const) integer(padded[key], `navigation.${key}`);
    if (padded.minX !== value.boundsMicroWU.min.x - NAVIGATION_PADDING_MICROWU || padded.maxX !== value.boundsMicroWU.max.x + NAVIGATION_PADDING_MICROWU ||
        padded.minZ !== value.boundsMicroWU.min.z - NAVIGATION_PADDING_MICROWU || padded.maxZ !== value.boundsMicroWU.max.z + NAVIGATION_PADDING_MICROWU) throw new Error("navigation: invalid-navigation");
  }

  assertRecord(value.budget, ["atlasId", "drawSubmissions", "materials", "profile", "textureBytes", "triangles"], "budget");
  if (value.budget.atlasId !== null) text(value.budget.atlasId, "budget.atlasId");
  integer(value.budget.drawSubmissions, "budget.drawSubmissions", 1); integer(value.budget.materials, "budget.materials", 1);
  integer(value.budget.textureBytes, "budget.textureBytes", 0); integer(value.budget.triangles, "budget.triangles", 1);
  if (value.budget.profile !== family) throw new Error("budget: invalid-budget");
  const requiredAtlas = REQUIRED_ATLAS[family] ?? null;
  if (value.budget.atlasId !== requiredAtlas || (requiredAtlas !== null && value.textures.some((item) => (item as TextureDescriptor).atlasId !== requiredAtlas))) throw new Error("budget: invalid-budget");
  const sums = {
    draws: value.geometry.reduce((sum, item) => sum + (item as GeometryDescriptor).drawSubmissions, 0),
    triangles: value.geometry.reduce((sum, item) => sum + (item as GeometryDescriptor).triangles, 0),
    textureBytes: value.textures.reduce((sum, item) => sum + (item as TextureDescriptor).bytes, 0),
  };
  const limits = PROFILE_LIMITS[family];
  if (value.budget.drawSubmissions !== sums.draws || value.budget.triangles !== sums.triangles || value.budget.textureBytes !== sums.textureBytes ||
      value.budget.materials !== value.materials.length || sums.draws > limits.draws || sums.triangles > limits.triangles ||
      value.materials.length > limits.materials || (limits.textureBytes !== null && sums.textureBytes > limits.textureBytes)) throw new Error("budget: invalid-budget");

  assertRecord(value.provenance, ["authorshipAttestation", "generatedAt", "generationRoute", "generatorCommit", "promptOrBriefPath", "promptOrBriefSha256", "referenceInputs", "rightsBasis", "sourcePath", "sourceSha256"], "provenance");
  for (const key of ["authorshipAttestation", "generatedAt", "generationRoute", "generatorCommit", "promptOrBriefPath", "rightsBasis", "sourcePath"] as const) text(value.provenance[key], `provenance.${key}`);
  sha(value.provenance.promptOrBriefSha256, "provenance.promptOrBriefSha256"); sha(value.provenance.sourceSha256, "provenance.sourceSha256");
  stringArray(value.provenance.referenceInputs, "provenance.referenceInputs");
  const generatedAt = value.provenance.generatedAt as string;
  const sourcePath = value.provenance.sourcePath as string;
  const promptOrBriefPath = value.provenance.promptOrBriefPath as string;
  if (value.provenance.referenceInputs.length !== 0 || value.provenance.authorshipAttestation !== "project-original-no-reference-reconstruction-v1" ||
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(generatedAt) || Number.isNaN(Date.parse(generatedAt)) || new Date(generatedAt).toISOString() !== generatedAt) throw new Error("provenance: malformed-schema");
  const projectAuthored = value.provenance.generationRoute === "project-authored" && value.provenance.generatorCommit === "project-authored" && value.provenance.rightsBasis === "project-original-authorship";
  const localGenerator = value.provenance.generationRoute === "local-img2threejs-d6673386" && value.provenance.generatorCommit === "d6673386f89673a58736f8d398dd16ece67874f5" && value.provenance.rightsBasis === "project-original-brief-and-local-apache-2.0-tool-output";
  if (!projectAuthored && !localGenerator) throw new Error("provenance: route-rights-mismatch");
  const validContainedPath = (path: string, roots: readonly string[]) => !path.includes("\\") && !/[?#:%\0]/.test(path) && !path.startsWith("/") && !path.split("/").some((segment) => segment === "" || segment === "." || segment === "..") && roots.some((root) => path.startsWith(root));
  if (!validContainedPath(sourcePath, ["scripts/asset-pipeline/fixtures/original/", "assets/art/first-yard/source/", "assets/art/generalist/source/", "assets/art/second-yard/source/"]) ||
      !validContainedPath(promptOrBriefPath, ["scripts/asset-pipeline/briefs/", "assets/art/first-yard/briefs/", "assets/art/generalist/briefs/", "assets/art/second-yard/briefs/"]) ||
      !/\.(?:md|txt)$/.test(promptOrBriefPath)) throw new Error("provenance: path-boundary");
  assertRecord(value.review, ["canonicalGeometrySha256", "fixtureSha256", "socketChainSha256"], "review");
  sha(value.review.canonicalGeometrySha256, "review.canonicalGeometrySha256"); sha(value.review.fixtureSha256, "review.fixtureSha256"); sha(value.review.socketChainSha256, "review.socketChainSha256");
  return value as FarmAssetManifest;
}

function normalizeCanonical(value: unknown): unknown {
  if (value === null || typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || Object.is(value, -0)) throw new Error("canonical-json: invalid-number");
    return value;
  }
  if (typeof value === "string") {
    if (hasLoneSurrogate(value)) throw new Error("canonical-json: invalid-string");
    return value.normalize("NFC");
  }
  if (Array.isArray(value)) return value.map(normalizeCanonical);
  if (typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype) {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      if (!/^[\x20-\x7E]+$/.test(key)) throw new Error("canonical-json: invalid-key");
      const item = (value as Record<string, unknown>)[key];
      if (item === undefined) throw new Error("canonical-json: undefined");
      result[key] = normalizeCanonical(item);
    }
    return result;
  }
  throw new Error("canonical-json: unsupported-value");
}

export function canonicalManifestJson(value: unknown): string {
  return JSON.stringify(normalizeCanonical(value));
}
